import { useMutation, useQueryClient } from "@tanstack/react-query";
import { bankingAccountsKeys } from "Api/Queries/Accounts/useBankingAccounts";
import {
  type BankAccountListItem,
  type EntityListOfBankAccountListItem,
  putBankingAccountsRank,
} from "Infrastructure/Api/Api";
import { processResponseWithApiProblemDetails } from "Infrastructure/Api/ApiUtils";
import { Resources, useResource } from "Translations/Resources";
import { toast } from "sonner";

export const useBankingAccountsOrder = () => {
  const queryClient = useQueryClient();
  const { t } = useResource();

  return useMutation({
    mutationFn: async (items: BankAccountListItem[]) => {
      await queryClient.cancelQueries({
        queryKey: bankingAccountsKeys.all,
      });
      // Optimistically reorder the self-view list entry (get/set are
      // exact-key, unlike the prefix-matching cancel/invalidate).
      queryClient.setQueryData<EntityListOfBankAccountListItem>(
        bankingAccountsKeys.listByUserID(),
        (accounts) => accounts && { ...accounts, items },
      );

      await processResponseWithApiProblemDetails(() =>
        putBankingAccountsRank({
          bankAccountIDs: items.map((i) => i.bankAccountID),
        }),
      )();
    },
    // The optimistic reorder is rolled back by the invalidation below - tell
    // the user why the list snapped back.
    onError: () => toast.error(t(Resources.Common.SaveError)),
    onSettled: (_, error) => {
      if (error) {
        queryClient.invalidateQueries({
          queryKey: bankingAccountsKeys.all,
        });
      }
    },
  });
};
